/**
 * Presentation helpers.
 *
 * Every timestamp the API emits is ISO-8601 with six fractional digits and a
 * literal Z, which `new Date()` parses natively. Nothing here ever reformats a
 * value that is going to be sent back to the server — these are display-only.
 */

/** Minor units (cents) to a display price, e.g. 399 -> "$3.99". */
export function formatMoney(minorUnits: number, currency = 'usd'): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    }).format(minorUnits / 100)
  } catch {
    return `$${(minorUnits / 100).toFixed(2)}`
  }
}

/** `13 Feb 2026`. Returns an em dash for a null or unparseable value. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

/** `13 Feb 2026, 10:30`. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const RELATIVE_STEPS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 7],
  ['week', 4.348],
  ['month', 12],
  ['year', Number.POSITIVE_INFINITY],
]

/** `in 3 days` / `2 hours ago`. */
export function formatRelative(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'

  let delta = (date.getTime() - Date.now()) / 1000
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  for (const [unit, size] of RELATIVE_STEPS) {
    if (Math.abs(delta) < size) {
      return formatter.format(Math.round(delta), unit)
    }
    delta /= size
  }
  return formatter.format(Math.round(delta), 'year')
}

/** `1.4 GB`. Binary units, because that is what a storage quota means. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  const value = bytes / 1024 ** index
  return `${value >= 10 || index === 0 ? Math.round(value) : value.toFixed(1)} ${units[index]}`
}

/** Up to two initials, for avatar chips. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

/** `owner` -> `Owner`. */
export function titleCase(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word[0]!.toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Formats a limit for display. The API expresses "no cap" as 0 (and older
 * payloads as -1); neither must ever be rendered literally.
 */
export function formatLimit(limit: number): string {
  return limit <= 0 ? 'Unlimited' : new Intl.NumberFormat().format(limit)
}

/** `10 seats`, or `Unlimited seats` when the tier has no cap. */
export function formatSeats(limit: number): string {
  if (limit <= 0) return 'Unlimited seats'
  return `${limit} ${limit === 1 ? 'seat' : 'seats'}`
}
